/**
 * Привязка OAuth-аккаунта — это полный редирект на провайдера и обратно (см. `useOAuth`),
 * то есть страница перемонтируется с нуля. Всё, что человек успел набрать в форме, к этому
 * моменту живёт только в памяти вкладки и умирает.
 *
 * Перед уходом на провайдера экран оставляет здесь «записку»: кто уходил и что нужно
 * восстановить, когда вернёмся. На возврате экран забирает записку (одноразово) и
 * поднимает своё состояние — шаг формы и черновик.
 *
 * sessionStorage, а не localStorage: записка нужна ровно на время этой поездки и не должна
 * всплывать в другой вкладке или завтра.
 */
const KEY = 'oauth-return-intent';

/**
 * Записка старше этого срока не поднимает ничего. Поездка на провайдера занимает минуты;
 * всё, что живёт дольше, — это брошенная вкладка, и восстанавливать там нечего. Заодно
 * сужает окно, в котором за тем же компьютером успевает сесть другой человек.
 */
const MAX_AGE_MS = 15 * 60 * 1000;

/** Убрать записку, не заглядывая внутрь. Хранилище может быть закрыто — это не ошибка. */
const drop = () => {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Хранилище недоступно (приватный режим, заблокированные куки): удалять нечего.
  }
};

/** Экран, который уходил на привязку. Один экран — один контекст. */
export const OAuthReturnContext = {
  /** Форма создания/редактирования заявки (`PostForm`). */
  POST_FORM: 'post-form',
} as const;

export type OAuthReturnContextValue = (typeof OAuthReturnContext)[keyof typeof OAuthReturnContext];

export type OAuthReturnIntent<TPayload = Record<string, unknown>> = {
  context: OAuthReturnContextValue;
  /** Что восстановить на возврате. Должно переживать JSON — Date кладём строкой. */
  payload?: TPayload;
  /** Когда записку оставили. Проставляется здесь, читается только для срока годности. */
  ts?: number;
};

/** Оставить записку перед уходом на провайдера. Вызывается из обработчика клика. */
export const markOAuthReturn = <TPayload>(intent: OAuthReturnIntent<TPayload>) => {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ ...intent, ts: Date.now() }));
  } catch {
    // Хранилище недоступно (приватный режим, квота): просто потеряем черновик, как раньше.
  }
};

/** Выбросить записку не читая — например, при выходе из аккаунта. */
export const clearOAuthReturn = drop;

/**
 * Забрать записку, если она от этого экрана и ещё свежая. Одноразово: прочитали — удалили,
 * чтобы следующий заход на ту же страницу начинался с чистого листа.
 */
export const consumeOAuthReturn = <TPayload>(
  context: OAuthReturnContextValue,
  /**
   * Дополнительная проверка: записка забирается только если она правда для этого экрана.
   * Без неё однофамилец (другая форма того же контекста) съедал бы чужую записку и своим
   * же сбросом стирал черновик, который она защищала.
   */
  isMine?: (payload: TPayload | undefined) => boolean
): OAuthReturnIntent<TPayload> | null => {
  let raw: string | null = null;

  try {
    raw = sessionStorage.getItem(KEY);
  } catch {
    // Само хранилище закрыто: читать нечего и удалять нечего.
    return null;
  }

  if (!raw) {
    return null;
  }

  try {
    const intent = JSON.parse(raw) as OAuthReturnIntent<TPayload>;

    // Чужая записка остаётся лежать: её заберёт тот экран, которому она адресована.
    if (intent.context !== context || (isMine != null && !isMine(intent.payload))) {
      return null;
    }

    drop();

    return intent.ts != null && Date.now() - intent.ts > MAX_AGE_MS ? null : intent;
  } catch {
    // Мусор вместо JSON — выкидываем, чтобы он не мешал следующей поездке.
    drop();
    return null;
  }
};
